import { usePresetAccess } from '#~/control/preset/use-access.js';
import { Sel, STagNew } from '#~/stag/index.js';
import { Availability } from '../availability.jsx';
import { Message, usePrompt } from '@studio/design-system/prompt';
import type { Group as M_Group } from '@studio/models/control/preset';
import { useLatestFn, useLazyRef } from '@studio/react-utils/hooks';
import type { T_Nullable } from '@tryiris-ai/js-types';
import { pick } from 'lodash-es';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import type { Wiring } from '../types.js';
import { useWiring } from '../wiring.js';
import { List } from './list.jsx';
import type { Props } from './types.js';
import { useAnalytics, EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/index.js';
import type { DeviceProps } from '#~/device/components/index.js';
import { useSelectedVideo } from '#~/room/hooks.js';

const Inner = ({ wiring, ...props }: Props & { wiring: Wiring }): ReactElement => {
  const { groups, selection } = wiring;
  const prompt = usePrompt();
  const { track } = useAnalytics();

  // Stabilize handlers.
  const handleAddGroup = useLatestFn(() => {
    wiring.addGroup();
    // Track the add preset group event
    track(EVENT_NAMES.ADD_PRESET_GROUP, EVENT_CATEGORIES.MOVEMENT_TOOLS);
  });

  const handleAddPreset = useLatestFn(wiring.addPreset);
  const handleRename = useLatestFn(wiring.renameGroup);
  const handleSelect = useLatestFn(wiring.selectGroup);
  const handleReorder = useLatestFn(wiring.reorderGroups);

  const handleRemove = useLatestFn((groupId: M_Group['id']) => {
    const group = groups.find((µ) => µ.id === groupId);

    if (!group) {
      return;
    }

    prompt.confirm({
      // Per playwright-stag-selectors-only (ai.knowledge/platform): assert the chosen group and click the real answers.
      ...STagNew(Sel.MainRoomBottomTrayPresets, 'group-delete', group.id),
      cancel: { label: 'Cancel', ...STagNew(Sel.MainRoomBottomTrayPresets, 'group-delete.cancel') },
      message: (
        <Message>
          Are you sure you want to delete the preset group
          <br />
          <strong>&ldquo;{group.name}&rdquo;</strong>?
        </Message>
      ),
      okay: {
        ...STagNew(Sel.MainRoomBottomTrayPresets, 'group-delete.confirm'),
        label: 'Delete Group',
        variant: 'danger'
      },
      onConfirm: (answer: boolean) => {
        if (!answer) {
          return;
        }

        wiring.removeGroup(group.id);
      },
      title: 'Delete preset group'
    });
  });

  const handler = useLazyRef(() => ({
    onAdd: handleAddGroup,
    onAddPreset: handleAddPreset,
    onRemove: handleRemove,
    onRename: handleRename,
    onReorder: handleReorder,
    onSelect: handleSelect
  })).current;

  // Be very careful w/ memoization here!
  return (
    <List
      groups={useMemo(() => groups.map((µ) => pick(µ, ['id', 'name'])), [groups])}
      handler={handler}
      selectedId={selection.groupId}
      {...props}
    />
  );
};

/** Interim guard. */
const Guard = ({ deviceId, ...props }: DeviceProps & Props): T_Nullable<ReactElement> => {
  const wiring = useWiring(deviceId);

  const access = usePresetAccess(deviceId);
  // The gate's wrapper boxes sit between the tab row and the group list, so
  // they must grow, stretch and shrink like the list does. Plain block boxes
  // collapse the list to its content size: the add-group button lands mid-row
  // and the group tabs ride above the row's vertical center.
  return (
    <Availability access={access} className='flex min-w-0 grow'>
      {wiring ? <Inner wiring={wiring} {...props} /> : null}
    </Availability>
  );
};

/** Position `<Groups>` component. */
export const Groups = (props: Props): T_Nullable<ReactElement> => {
  const [deviceId] = useSelectedVideo();

  return deviceId ? <Guard deviceId={deviceId} {...props} /> : null;
};
